/**
 * useCalendars Hook
 *
 * Fetches and manages the user's calendar collections.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { api, CalendarCollectionDetail, CalendarCollectionInput } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

/**
 * Cross-instance change notifier. This hook is per-component STATE, not a
 * shared cache — every component calling useCalendars() holds its own copy,
 * so a calendar created in one place (the sync picker's create-calendar
 * escape hatch, say) was invisible to the sidebar until a full reload.
 * Mutations notify every OTHER mounted instance to refetch; the mutating
 * instance already updated its own state in place.
 */
const changeListeners = new Set<() => void>();
function notifyOtherInstances(self: () => void): void {
  for (const listener of changeListeners) {
    if (listener !== self) listener();
  }
}

export function useCalendars() {
  const [calendars, setCalendars] = useState<CalendarCollectionDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCalendars = useCallback(async () => {
    try {
      setLoading(true);
      const result = await api.listCalendarCollections();
      setCalendars(result.calendars);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load calendars');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalendars();
    changeListeners.add(fetchCalendars);
    return () => {
      changeListeners.delete(fetchCalendars);
    };
  }, [fetchCalendars]);

  const createCalendar = useCallback(
    async (input: CalendarCollectionInput) => {
      const created = await api.createCalendarCollection(input);
      setCalendars((prev) => [...prev, created]);
      notifyOtherInstances(fetchCalendars);
      return created;
    },
    [fetchCalendars],
  );

  const updateCalendar = useCallback(
    async (id: string, input: Partial<CalendarCollectionInput>) => {
      const updated = await api.updateCalendarCollection(id, input);
      setCalendars((prev) => prev.map((c) => (c.id === id ? updated : c)));
      notifyOtherInstances(fetchCalendars);
      return updated;
    },
    [fetchCalendars],
  );

  const deleteCalendar = useCallback(
    async (id: string, deleteEvents = false) => {
      await api.deleteCalendarCollection(id, deleteEvents);
      setCalendars((prev) => prev.filter((c) => c.id !== id));
      notifyOtherInstances(fetchCalendars);
    },
    [fetchCalendars],
  );

  const toggleVisibility = useCallback(
    async (id: string) => {
      const calendar = calendars.find((c) => c.id === id);
      if (!calendar) return;
      try {
        await updateCalendar(id, { isVisible: !calendar.isVisible });
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to toggle calendar visibility',
          variant: 'destructive',
        });
      }
    },
    [calendars, updateCalendar],
  );

  const visibleCalendarIds = useMemo(
    () => calendars.filter((c) => c.isVisible).map((c) => c.id),
    [calendars],
  );

  return {
    calendars,
    loading,
    error,
    fetchCalendars,
    createCalendar,
    updateCalendar,
    deleteCalendar,
    toggleVisibility,
    visibleCalendarIds,
  };
}
